"use client";

import { Component, Suspense, lazy, type ReactNode } from "react";
import type { Application } from "@splinetool/runtime";

const Spline = lazy(() => import("@splinetool/react-spline"));

interface SplineSceneProps {
  scene: string;
  className?: string;
  onLoad?: (app: Application) => void;
  onError?: () => void;
  renderOnDemand?: boolean;
}

interface BoundaryProps {
  children: ReactNode;
  onError?: () => void;
}

/* A failed scene fetch (offline, ad-blocker, firewall) must degrade to the
   static fallback instead of bubbling up and replacing the whole page with
   the framework error screen. */
class SplineErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError?.();
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export function SplineScene({
  scene,
  className,
  onLoad,
  onError,
  renderOnDemand = false,
}: SplineSceneProps) {
  return (
    <SplineErrorBoundary onError={onError}>
      <Suspense
        fallback={
          <div className="flex h-full w-full items-center justify-center">
            <span className="loader" />
          </div>
        }
      >
        <Spline
          scene={scene}
          className={className}
          onLoad={onLoad}
          renderOnDemand={renderOnDemand}
        />
      </Suspense>
    </SplineErrorBoundary>
  );
}
